import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FollowupEmailModal } from "../components/FollowupEmailModal";
import { OpportunityFormModal } from "../components/OpportunityFormModal";
import { RescheduleModal } from "../components/RescheduleModal";
import { api } from "../lib/api";
import { CADENCES } from "../lib/cadences";
import { contractInAviso, FAROL_COLORS, formatContractMonth } from "../lib/contract";
import { FollowUp, Opportunity } from "../types/crm";

type TabKey = "hoje" | "7dias" | "todos" | "cadencias";

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function daysBetween(fromISO: string, toISO: string): number {
  const a = new Date(`${fromISO}T00:00:00`);
  const b = new Date(`${toISO}T00:00:00`);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function ddmm(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

interface Row {
  key: string;
  opportunity: Opportunity;
  followup: FollowUp | null;
  atrasoDias: number; // 0 se não vencido
  isContractAviso: boolean;
}

function situacao(row: Row): { text: string; bg: string; color: string } {
  if (row.atrasoDias > 0)
    return { text: `Atrasado ${row.atrasoDias}d`, bg: "#FEE2E2", color: "#DC2626" };
  if (row.isContractAviso) return { text: "Contrato vence", bg: "#FEF3C7", color: "#92400E" };
  if (row.opportunity.status === "ganha") return { text: "Ganha", bg: "#DCFCE7", color: "#166534" };
  if (!row.followup) return { text: "Sem FUP", bg: "#F1F5F9", color: "#94A3B8" };
  return { text: "Agendado", bg: "#F1F5F9", color: "#64748B" };
}

export function FollowUpsPage() {
  const [tab, setTab] = useState<TabKey>("hoje");
  const [followups, setFollowups] = useState<FollowUp[]>([]);
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rescheduling, setRescheduling] = useState<FollowUp | null>(null);
  const [emailing, setEmailing] = useState<Opportunity | null>(null);
  const [editing, setEditing] = useState<Opportunity | null>(null);

  const today = todayISO();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [fupRes, oppRes] = await Promise.all([
        api.get<FollowUp[]>("/followups/", { params: { status: "pendente" } }),
        api.get<Opportunity[]>("/opportunities/"),
      ]);
      setFollowups(fupRes.data);
      setOpps(oppRes.data);
    } catch {
      setError("Não foi possível carregar os follow-ups.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const oppById = useMemo(() => {
    const map = new Map<number, Opportunity>();
    opps.forEach((o) => map.set(o.id, o));
    return map;
  }, [opps]);

  // Linhas de follow-up (pendentes) com a oportunidade completa.
  const fupRows = useMemo<Row[]>(() => {
    return followups
      .map((f) => {
        const opp = oppById.get(f.opportunity);
        if (!opp) return null;
        const atraso = daysBetween(f.due_date, today);
        return {
          key: `fup-${f.id}`,
          opportunity: opp,
          followup: f,
          atrasoDias: atraso > 0 ? atraso : 0,
          isContractAviso: !!opp.contract_end && contractInAviso(opp.contract_end),
        } as Row;
      })
      .filter((r): r is Row => r !== null);
  }, [followups, oppById, today]);

  // Oportunidades abertas com contrato a vencer (entram em "Hoje" mesmo sem FUP).
  const contractRows = useMemo<Row[]>(() => {
    const withFup = new Set(fupRows.map((r) => r.opportunity.id));
    return opps
      .filter(
        (o) =>
          o.status === "aberta" &&
          o.contract_end &&
          contractInAviso(o.contract_end) &&
          !withFup.has(o.id)
      )
      .map((o) => ({
        key: `contract-${o.id}`,
        opportunity: o,
        followup: null,
        atrasoDias: 0,
        isContractAviso: true,
      }));
  }, [opps, fupRows]);

  const overdueRows = fupRows.filter((r) => r.atrasoDias > 0);
  const todayRows = [
    ...overdueRows,
    ...fupRows.filter((r) => r.atrasoDias === 0 && r.followup?.due_date === today),
    ...contractRows,
  ];
  const next7Rows = fupRows.filter((r) => {
    if (!r.followup) return false;
    const diff = daysBetween(today, r.followup.due_date);
    return diff > 0 && diff <= 7;
  });
  const allRows = [...fupRows, ...contractRows];

  const todayCount = overdueRows.length + contractRows.length;

  const handleReschedule = async (followupId: number, dueDate: string) => {
    const response = await api.post<FollowUp>(`/followups/${followupId}/reagendar/`, {
      due_date: dueDate,
    });
    setFollowups((current) => current.map((f) => (f.id === response.data.id ? response.data : f)));
  };

  const rowsForTab = tab === "hoje" ? todayRows : tab === "7dias" ? next7Rows : allRows;

  const tabs: { key: TabKey; label: string; badge?: number }[] = [
    { key: "hoje", label: "Hoje", badge: todayCount },
    { key: "7dias", label: "Próximos 7 dias", badge: next7Rows.length },
    { key: "todos", label: "Todos", badge: allRows.length },
    { key: "cadencias", label: "Cadências" },
  ];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-800">Follow-ups</h1>
        <span className="flex items-center gap-1.5 text-xs text-accent-dark">
          <span className="inline-block h-2 w-2 rounded-full bg-accent" />
          Outlook conectado · rascunhos exigem revisão
        </span>
      </div>

      {/* Abas */}
      <div className="mt-4 flex flex-wrap gap-2 border-b border-border-subtle">
        {tabs.map((t) => {
          const active = tab === t.key;
          const danger = t.key === "hoje" && overdueRows.length > 0;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
                active
                  ? danger
                    ? "border-red-500 text-red-600"
                    : "border-primary text-primary"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
              {t.badge !== undefined && t.badge > 0 && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                    danger
                      ? active
                        ? "bg-red-500 text-white"
                        : "bg-[#FEE2E2] text-[#DC2626]"
                      : "bg-primary-soft text-primary"
                  }`}
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isLoading && <p className="mt-6 text-sm text-slate-500">Carregando...</p>}

      {!isLoading && error && (
        <div className="mt-6 rounded-[10px] border border-border-subtle bg-surface p-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="mt-3 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !error && tab === "cadencias" && <CadencesTab />}

      {!isLoading && !error && tab !== "cadencias" && (
        <div className="mt-5">
          {tab === "hoje" && overdueRows.length > 0 && (
            <div className="mb-4 rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">
              {overdueRows.length === 1
                ? "1 follow-up venceu e voltou para hoje."
                : `${overdueRows.length} follow-ups venceram e voltaram para hoje.`}
            </div>
          )}

          {rowsForTab.length === 0 ? (
            <div className="rounded-[10px] border border-border-subtle bg-surface p-8 text-center">
              <p className="text-sm font-medium text-slate-700">Nenhum follow-up para {tab === "hoje" ? "hoje" : "este período"}</p>
              <p className="mt-1 text-xs text-slate-500">Aproveite para adiantar os próximos 7 dias.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-[10px] border border-border-subtle">
              <table className="min-w-full divide-y divide-border-subtle text-sm">
                <thead className="bg-surface">
                  <tr>
                    {["Empresa", "Etapa", "Resp.", "FUP", "Situação", "Ações"].map((h) => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {rowsForTab.map((row) => {
                    const opp = row.opportunity;
                    const sit = situacao(row);
                    const farol = opp.farol ? FAROL_COLORS[opp.farol] : null;
                    const showEmail = tab === "hoje" || row.atrasoDias > 0 || row.isContractAviso;
                    return (
                      <tr key={row.key}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5">
                            {farol && (
                              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: farol.dot }} title={farol.label} />
                            )}
                            <span className="font-semibold text-slate-800">{opp.company_detail.name}</span>
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-xs">
                            {opp.products_detail[0] && (
                              <span className="rounded-full bg-primary-soft px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                {opp.products_detail[0].name}
                              </span>
                            )}
                            <span className={opp.primary_contact_detail?.name ? "text-slate-500" : "text-[#94A3B8]"}>
                              {opp.primary_contact_detail?.name ?? "— (a preencher)"}
                            </span>
                            {opp.contract_end && (
                              <span className="text-[#92400E]">· {formatContractMonth(opp.contract_end)}</span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2">
                          <span className="rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary">
                            {opp.stage_detail.name}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-soft text-[10px] font-semibold uppercase text-primary"
                            title={opp.owner_detail.username}
                          >
                            {opp.owner_detail.username.slice(0, 2)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-slate-600">
                          {row.followup ? ddmm(row.followup.due_date) : "—"}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{ backgroundColor: sit.bg, color: sit.color }}
                          >
                            {sit.text}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {showEmail && (
                              <button
                                type="button"
                                onClick={() => setEmailing(opp)}
                                title="Preparar e-mail de follow-up"
                                className="rounded-[8px] bg-primary px-2 py-1 text-xs font-medium text-white hover:bg-primary-dark"
                              >
                                ✉ E-mail
                              </button>
                            )}
                            {row.followup && (
                              <button
                                type="button"
                                onClick={() => setRescheduling(row.followup)}
                                title="Reagendar follow-up"
                                className="text-slate-500 hover:text-primary"
                              >
                                🗓
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setEditing(opp)}
                              title="Abrir oportunidade"
                              className="text-slate-500 hover:text-primary"
                            >
                              →
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {rescheduling && (
        <RescheduleModal
          companyName={rescheduling.opportunity_detail.company_detail.name}
          currentDueDate={rescheduling.due_date}
          onCancel={() => setRescheduling(null)}
          onConfirm={async (dueDate) => {
            await handleReschedule(rescheduling.id, dueDate);
            setRescheduling(null);
          }}
        />
      )}

      {emailing && (
        <FollowupEmailModal
          opportunity={emailing}
          onClose={() => setEmailing(null)}
          onReschedule={async (dueDate) => {
            const fup = followups.find((f) => f.opportunity === emailing.id);
            if (fup) await handleReschedule(fup.id, dueDate);
          }}
        />
      )}

      {editing && (
        <OpportunityFormModal
          opportunity={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await fetchData();
          }}
        />
      )}
    </div>
  );
}

function channelPill(canal: string): { bg: string; color: string } {
  return canal === "WhatsApp"
    ? { bg: "#DCFCE7", color: "#166534" }
    : { bg: "#EFF6FF", color: "#1D4ED8" };
}

function CadencesTab() {
  return (
    <div className="mt-5 space-y-6">
      <div className="rounded-[10px] bg-primary-soft px-4 py-3 text-sm text-primary">
        <strong>Modelos disponíveis na revisão do e-mail.</strong> Ao preparar um e-mail, o
        responsável escolhe qual toque da cadência aplicar. Os campos [entre colchetes] são
        preenchidos na revisão — nada é enviado sem aprovação.
      </div>

      {CADENCES.map((cadence) => (
        <div key={cadence.key} className="overflow-x-auto rounded-[10px] border border-border-subtle">
          <div className="border-b border-border-subtle bg-surface px-4 py-2">
            <p className="text-sm font-semibold text-slate-800">
              Cadência · {cadence.nome}
            </p>
            <p className="text-xs text-slate-500">{cadence.contexto}</p>
          </div>
          <table className="min-w-full divide-y divide-border-subtle text-sm">
            <thead className="bg-surface">
              <tr>
                {["#", "Dia", "Canal", "Objetivo", "Sugestão de copy"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {cadence.touches.map((t) => {
                const pill = channelPill(t.canal);
                return (
                  <tr key={t.n}>
                    <td className="px-4 py-2 text-slate-500">{t.n}</td>
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-700">{t.dia}</td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <span className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: pill.bg, color: pill.color }}>
                        {t.canal}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{t.objetivo}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {t.assunto && <p className="font-medium text-slate-700">{t.assunto}</p>}
                      <p>{t.copy}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
